export interface SoftwareRequirement {
    id: string;
    name: string;
    shortName: string;
    stepNumber: string;
    sectionTitle: string;
    description: string;
    summary: string;
    videoUrl: string;
    videoId: string;
    videoTitle: string;
    badge: string;
    downloadUrl?: string;
    downloadLabel?: string;
    systemRequirements?: string[];
}

export interface SidebarChecklistItem {
    id: string;
    name: string;
    path: string;
    iconName: 'BookOpen' | 'Video' | 'Mic2' | 'ClipboardList';
    description: string;
}

export interface Day1ReadinessConfig {
    pageTag: string;
    pageTitle: string;
    pageSubtitle: string;
    dashboardCard: {
        title: string;
        description: string;
        ctaText: string;
        badge: string;
    };
    softwareSection: {
        number: string;
        title: string;
        instruction: string;
    };
    softwareList: SoftwareRequirement[];
    finalExpectationSection: {
        title: string;
        instruction: string;
        checklist: SidebarChecklistItem[];
    };
    finalCallout: {
        title: string;
        subtitle: string;
        encouragement: string;
    };
}

export const readinessConfig: Day1ReadinessConfig = {
    pageTag: 'PRE-COHORT READINESS REQUIREMENTS',
    pageTitle: 'LGL | DAY 1 CLASS READINESS',
    pageSubtitle: 'Preparation requirements for every participant before Day One of the cohort.',
    dashboardCard: {
        title: 'Day 1 Class Readiness',
        description: 'Get prepared for your first class. Complete the required software installations and make sure your dashboard is ready before Day One.',
        ctaText: 'View Day 1 Readiness',
        badge: 'Required Pre-Class Guide'
    },
    softwareSection: {
        number: '01',
        title: '01 — Software Installation',
        instruction: 'You are required to install the following software before Day One. Please note that Primavera P6 and Microsoft Project (MSP) are native Windows applications—Mac (macOS) users must install Parallels Desktop with a Windows environment before software installation can commence.'
    },
    softwareList: [
        {
            id: 'primavera-p6',
            name: 'Primavera P6',
            shortName: 'P6',
            stepNumber: '02',
            sectionTitle: '02 — Primavera P6 Installation',
            description: 'Follow the steps in the LGL YouTube video below to install Primavera P6. (Mac users: Parallels Desktop with Windows must be installed first before starting the P6 installation).',
            summary: 'Industry-standard enterprise project portfolio management (PPM) software for planning, scheduling, and resource control.',
            downloadUrl: 'https://bit.ly/4qPb0kZ',
            downloadLabel: 'Download Primavera P6',
            // Centralized YouTube video URL/ID - easily updated here
            videoUrl: 'https://youtu.be/0HYX0UZcrcI',
            videoId: '0HYX0UZcrcI',
            videoTitle: 'Primavera P6 Installation Guide',
            badge: 'Essential PPM Tool',
            systemRequirements: ['Windows 10 / 11 (64-bit) — Mac requires Parallels Desktop', '4GB RAM Minimum (8GB Recommended)', '2GB Free Disk Space']
        },
        {
            id: 'microsoft-project',
            name: 'Microsoft Project (MSP)',
            shortName: 'MSP',
            stepNumber: '03',
            sectionTitle: '03 — Microsoft Project (MSP) Installation',
            description: 'Follow the steps in the LGL YouTube video below to install Microsoft Project (MSP). (Mac users: Parallels Desktop with Windows must be installed first before starting the MSP installation).',
            summary: 'Comprehensive project management and Gantt charting application used extensively across modern industry projects.',
            downloadUrl: 'https://bit.ly/4zTNLdx',
            downloadLabel: 'Download Microsoft Project (MSP)',
            // Centralized YouTube video URL/ID - easily updated here
            videoUrl: 'https://youtu.be/C5U3bCXGqRc',
            videoId: 'C5U3bCXGqRc',
            videoTitle: 'Microsoft Project (MSP) Installation Guide',
            badge: 'Core Scheduling Tool',
            systemRequirements: ['Windows 10 / 11 — Mac requires Parallels Desktop', 'Office 365 / Standalone License', '4GB RAM Minimum']
        },
        {
            id: 'microsoft-teams',
            name: 'Microsoft Teams',
            shortName: 'Teams',
            stepNumber: '04',
            sectionTitle: '04 — Microsoft Teams Installation',
            description: "Please follow the steps in the video below if you don't have Microsoft Teams installed on your computer.",
            summary: 'Official virtual classroom, live breakout sessions, and real-time collaboration hub for your LGL cohort.',
            downloadUrl: 'https://www.microsoft.com/en-us/microsoft-teams/download-app',
            downloadLabel: 'Download Microsoft Teams',
            // Exact requested YouTube video
            videoUrl: 'https://youtu.be/jKnV6H6wbNA?si=fXFJe5ZQ1qYGExas',
            videoId: 'jKnV6H6wbNA',
            videoTitle: 'Microsoft Teams Installation Guide',
            badge: 'Live Session & Collab',
            systemRequirements: ['Windows / macOS / iOS / Android', 'Webcam & Microphone', 'Stable Internet Connection']
        }
    ],
    finalExpectationSection: {
        title: 'FINAL DAY 1 READINESS EXPECTATION',
        instruction: 'Before attending the first class, you are expected to check your dashboard and make sure you can access the following sidebar menu items:',
        checklist: [
            {
                id: 'sidebar-course',
                name: 'Course',
                path: '/student/courses',
                iconName: 'BookOpen',
                description: 'Verify you can view your enrolled curriculum, syllabus modules, and on-demand video lessons.'
            },
            {
                id: 'sidebar-live',
                name: 'Live Session',
                path: '/student/live',
                iconName: 'Video',
                description: 'Ensure you can view upcoming scheduled live lecture times, join links, and class recordings.'
            },
            {
                id: 'sidebar-interview',
                name: 'Interview',
                path: '/student/interview',
                iconName: 'Mic2',
                description: 'Confirm access to interview preparation modules, career resources, and mentor review booking.'
            },
            {
                id: 'sidebar-assignment',
                name: 'Assignment',
                path: '/student/assignments',
                iconName: 'ClipboardList',
                description: 'Check that you can view cohort project briefs, submission portals, and instructor evaluation rubrics.'
            }
        ]
    },
    finalCallout: {
        title: 'BE READY BEFORE DAY ONE',
        subtitle: 'ARRIVE PREPARED TO LEARN, PRACTICE, AND PARTICIPATE',
        encouragement: 'Your instructor and peers will be diving straight into hands-on exercises from session one. Having your environment set up ensures you get the absolute most out of your training journey with Layos Global Learning.'
    }
};
